<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick"></DepartmentPane>
    </section>

    <section class="dp-right">
      <div v-if="!currentDept" class="no-data-tip">
        <i class="el-icon-receiving"></i>
        <p>请从左侧选择一个机构或班级进行管理</p>
      </div>

      <div v-else class="face-right-body">
        <!-- 学校、库状态、提示收成一块，避免上方空出两张大卡片。最后修改：2026-10-14 -->
        <div class="status-panel">
          <div class="status-row">
            <div class="title">
              <i class="el-icon-school"></i>
              <span>{{ currentSchoolName || '未识别学校' }}</span>
              <span class="sub-title">人脸库</span>
            </div>
            <div class="info-item">
              <span class="info-label">库名称</span>
              <span class="info-value" :class="{ 'is-empty': !faceDbInfo.dbName }">{{ faceDbInfo.dbName || '未初始化' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">当前部门</span>
              <span class="info-value">{{ currentDeptName }}</span>
            </div>
            <el-button
              v-if="faceDbInfoLoaded && faceCompareEnabled && !faceDbInfo.dbName"
              type="primary"
              size="small"
              :loading="creatingDb"
              icon="el-icon-magic-stick"
              @click="handleCreateFaceDb"
            >初始化人脸库</el-button>
            <div v-if="faceDbInfoLoaded && faceCompareEnabled && faceDbInfo.dbName" class="info-action-btns">
              <el-button
                type="primary"
                size="small"
                icon="el-icon-refresh"
                :disabled="!isCurrentDeptClass || batchSyncInProgress"
                :loading="batchSyncInProgress"
                @click="handleBatchAddFace"
              >{{ batchSyncInProgress ? '同步进行中…' : '批量同步' }}</el-button>
              <el-button
                type="default"
                plain
                size="small"
                icon="el-icon-tickets"
                :disabled="!isCurrentDeptClass"
                @click="openSyncRecordDialog"
              >同步记录</el-button>
              <el-button
                type="default"
                plain
                size="small"
                icon="el-icon-view"
                :loading="batchDetectLoading"
                :disabled="!isCurrentDeptClass || batchDetectLoading"
                @click="handleBatchDetectFace"
              >批量检测</el-button>
              <el-button
                type="success"
                plain
                size="small"
                icon="el-icon-upload2"
                @click="openSearchFaceDialog"
              >上传识别</el-button>
            </div>
          </div>
          <p v-if="!faceDbInfoLoaded && !listLoading" class="face-inline-tip is-info">
            <i class="el-icon-loading"></i>
            正在加载部门人脸配置…
          </p>
          <p v-else-if="!faceCompareEnabled" class="face-inline-tip">
            <i class="el-icon-warning-outline"></i>
            暂无人脸比对服务：本校未在「部门配置 - 人脸比对」中开通，无法新建、同步或初始化人脸库。下方列表仍显示历史同步状态。
          </p>
          <p v-else-if="batchSyncInProgress && activeSyncJob" class="face-inline-tip">
            <i class="el-icon-loading"></i>
            任务已创建，共 {{ activeSyncJob.TotalCount }} 人（后台持续更新）
          </p>
          <p v-else-if="faceDbInfo.dbName && !isCurrentDeptClass" class="face-inline-tip">
            <i class="el-icon-warning-outline"></i>
            批量同步、批量检测、同步记录仅支持班级，请从左侧选择具体班级。
          </p>
        </div>

        <!-- 列表筛选：统一 Toolbar，仅保留查询类操作。创建于 2026-08-02 -->
        <toolbar
          :buttonList="buttonList"
          :searchList="searchList"
          @callFunction="callFunction"
        />

        <div class="tbl-scroll-host">
        <el-table
          :data="staffList"
          v-loading="listLoading"
          height="100%" style="width: 100%"
        >
          <el-table-column prop="StuName" label="姓名" min-width="100" show-overflow-tooltip />
          <el-table-column prop="StuNo" label="学号" min-width="110" show-overflow-tooltip />
          <el-table-column prop="DepartmentName" label="部门" min-width="140" show-overflow-tooltip />
          <el-table-column prop="CardNo" label="卡号" min-width="120" show-overflow-tooltip />
          <el-table-column label="照片" width="80" align="center">
            <template slot-scope="scope">
              <!-- OSS 私有桶需预签名，列表统一用 oss-media-thumbs（与 Person.vue 一致）。最后修改：2026-09-14 -->
              <oss-media-thumbs
                v-if="scope.row.HeadPhotoImage"
                :media="scope.row.HeadPhotoImage"
                thumb-width="40px"
                thumb-height="40px"
              />
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>
          <el-table-column label="同步状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStatusType(scope.row)" size="mini">
                {{ getStatusText(scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="最后同步时间" min-width="160" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ formatTime(scope.row.faceRecord ? scope.row.faceRecord.LastSyncTime : null) }}
            </template>
          </el-table-column>
          <el-table-column
            v-if="faceDbInfoLoaded && faceCompareEnabled"
            label="操作"
            width="200"
            align="center"
          >
            <template slot-scope="scope">
              <div class="table-op-pair">
                <el-button type="text" class="table-op-link" size="mini" @click="handleSingleAddFace(scope.row)">同步</el-button>
                <el-button
                  type="text"
                  class="table-op-link"
                  size="mini"
                  :disabled="!scope.row.HeadPhotoImage || detectLoading"
                  @click="handleDetectRow(scope.row)"
                >检测</el-button>
                <el-button type="text" class="table-op-link table-op-link--danger" size="mini" @click="handleDeleteEntity(scope.row)">重置</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
        </div>

        <div class="pagination-container">
          <el-pagination
            layout="total, prev, pager, next"
            @current-change="handlePageChange"
            :current-page="page"
            :page-size="pageSize"
            :total="total"
            style="float: right"
          />
        </div>
      </div>

      <el-dialog
        :visible.sync="syncRecordDialogVisible"
        width="920px"
        append-to-body
        custom-class="face-sync-record-dialog"
        @open="onSyncRecordDialogOpen"
      >
        <div slot="title" class="sync-dialog-title">
          <span><i class="el-icon-document"></i> 同步记录</span>
          <el-button type="text" size="small" icon="el-icon-refresh" @click.stop="loadSyncJobList">刷新</el-button>
        </div>
        <el-table :data="syncJobList" size="small" border stripe height="420" class="face-sync-record-table">
          <el-table-column prop="StartTime" label="开始时间" width="160" :formatter="fmtTableTime" />
          <el-table-column prop="EndTime" label="结束时间" width="160" :formatter="fmtTableTime" />
          <el-table-column label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.Status === 0" type="warning" size="mini">进行中</el-tag>
              <el-tag v-else type="success" size="mini">已完成</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="TotalCount" label="总数" width="72" align="center" />
          <el-table-column prop="SuccessCount" label="成功" width="72" align="center" />
          <el-table-column prop="FailCount" label="失败" width="72" align="center" />
          <el-table-column prop="ErrorMessage" label="任务异常" min-width="140" show-overflow-tooltip />
          <el-table-column label="操作" width="100" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="mini" @click="openJobDetail(scope.row)">明细</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="sync-record-pager">
          <el-pagination
            small
            layout="total, prev, pager, next"
            :total="syncJobTotal"
            :page-size="syncJobPageSize"
            :current-page.sync="syncJobPage"
            @current-change="loadSyncJobList"
          />
        </div>
      </el-dialog>

      <el-dialog title="同步任务明细" :visible.sync="jobDetailVisible" width="760px" append-to-body>
        <el-table :data="jobItemList" size="small" border max-height="400">
          <el-table-column prop="PersonName" label="姓名" width="100" show-overflow-tooltip />
          <el-table-column prop="PersonId" label="人员ID" width="110" />
          <el-table-column label="结果" width="88" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.ProcessStatus === 1" type="success" size="mini">成功</el-tag>
              <el-tag v-else-if="scope.row.ProcessStatus === 2" type="danger" size="mini">失败</el-tag>
              <el-tag v-else type="info" size="mini">待处理</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="ResultMessage" label="说明" min-width="160" show-overflow-tooltip />
          <el-table-column prop="FinishedTime" label="完成时间" width="168" :formatter="fmtTableTime" />
        </el-table>
        <div class="sync-record-pager">
          <el-pagination
            small
            layout="total, prev, pager, next"
            :total="jobItemTotal"
            :page-size="jobItemPageSize"
            :current-page.sync="jobItemPage"
            @current-change="loadJobItemList"
          />
        </div>
      </el-dialog>

      <!-- 批量质量检测（对应当前部门）。创建于 2026-09-15 -->
      <el-dialog
        title="批量人脸质量检测"
        :visible.sync="batchDetectDialogVisible"
        width="860px"
        append-to-body
        :close-on-click-modal="!batchDetectLoading"
        :close-on-press-escape="!batchDetectLoading"
        :show-close="!batchDetectLoading"
      >
        <div v-if="batchDetectLoading" class="detect-loading">
          <i class="el-icon-loading"></i>
          <p>正在检测当前班级有头像的学生，请稍候…</p>
          <p class="detect-loading-sub">按约 4 次/秒限速，人数较多时需等待</p>
        </div>
        <div v-else>
          <div class="batch-detect-summary">
            <span>共 {{ batchDetectSummary.TotalCount || 0 }} 人</span>
            <el-tag type="success" size="mini">合格 {{ batchDetectSummary.QualifiedCount || 0 }}</el-tag>
            <el-tag type="danger" size="mini">不合格 {{ batchDetectSummary.UnqualifiedCount || 0 }}</el-tag>
            <el-radio-group v-model="batchDetectFilter" size="mini" style="margin-left: 12px;">
              <el-radio-button :label="0">全部</el-radio-button>
              <el-radio-button :label="2">不合格</el-radio-button>
              <el-radio-button :label="1">合格</el-radio-button>
            </el-radio-group>
          </div>
          <el-table :data="filteredBatchDetectItems" size="small" border height="420">
            <el-table-column prop="PersonName" label="姓名" width="100" show-overflow-tooltip />
            <el-table-column prop="PersonId" label="人员ID" width="100" />
            <el-table-column label="结果" width="88" align="center">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.IsQualified" type="success" size="mini">合格</el-tag>
                <el-tag v-else type="danger" size="mini">不合格</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="关键点分" width="90" align="center">
              <template slot-scope="scope">
                <span :class="scoreClass(scope.row.LandmarkScore)">{{ formatDetectScore(scope.row.LandmarkScore) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="综合质量" width="90" align="center">
              <template slot-scope="scope">
                <span :class="scoreClass(scope.row.QualityScore)">{{ formatDetectScore(scope.row.QualityScore) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="Summary" label="说明" min-width="200" show-overflow-tooltip />
          </el-table>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button type="primary" :disabled="batchDetectLoading" @click="batchDetectDialogVisible = false">关闭</el-button>
        </span>
      </el-dialog>

      <!-- 学校验证：上传图片后调用 SearchFaceByFile。创建于 2026-09-15 -->
      <el-dialog
        title="上传识别验证"
        :visible.sync="searchFaceDialogVisible"
        width="560px"
        append-to-body
        :close-on-click-modal="!searchFaceLoading"
        :close-on-press-escape="!searchFaceLoading"
        :show-close="!searchFaceLoading"
        @closed="onSearchFaceDialogClosed"
      >
        <p class="search-face-tip">上传现场照片，在本校人脸库中检索匹配人员（置信度阈值约 60.48）。</p>
        <el-upload
          class="search-face-uploader"
          drag
          action="#"
          :auto-upload="false"
          :show-file-list="false"
          accept="image/jpeg,image/jpg,image/png,image/bmp"
          :on-change="onSearchFaceFileChange"
          :disabled="searchFaceLoading"
        >
          <div v-if="searchFacePreviewUrl" class="search-face-preview-wrap">
            <img :src="searchFacePreviewUrl" class="search-face-media" alt="待识别照片" />
            <p class="search-face-tip-sub">点击或拖拽可更换图片</p>
          </div>
          <div v-else>
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将图片拖到此处，或<em>点击选择</em></div>
          </div>
          <div class="el-upload__tip" slot="tip">支持 JPG / PNG / BMP</div>
        </el-upload>
        <div v-if="searchFaceResult" class="search-face-result">
          <el-tag type="success" size="medium">匹配成功</el-tag>
          <div class="search-face-result-row">
            <oss-media-thumbs
              v-if="searchFaceResult.HeadPhotoImage"
              :media="searchFaceResult.HeadPhotoImage"
              thumb-width="56px"
              thumb-height="56px"
            />
            <div class="search-face-result-info">
              <div><strong>{{ searchFaceResult.Name || '—' }}</strong></div>
              <div>年级：{{ searchFaceResult.GradeName || '—' }}</div>
              <div>班级：{{ searchFaceResult.ClassName || '—' }}</div>
              <div>人员ID：{{ searchFaceResult.PersonId || '—' }}</div>
            </div>
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button :disabled="searchFaceLoading" @click="searchFaceDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="searchFaceLoading" :disabled="!searchFaceFile" @click="submitSearchFace">开始识别</el-button>
        </span>
      </el-dialog>

      <!-- DetectFace 单人结果（点击即弹出，先显示检测中）。创建于 2026-09-15 -->
      <el-dialog
        :title="detectResultTitle"
        :visible.sync="detectResultVisible"
        width="520px"
        append-to-body
        :close-on-click-modal="!detectLoading"
        :close-on-press-escape="!detectLoading"
        :show-close="!detectLoading"
      >
        <div v-if="detectLoading" class="detect-loading">
          <i class="el-icon-loading"></i>
          <p>检测中，请稍候…</p>
        </div>
        <div v-else-if="detectResult" class="detect-result">
          <div class="detect-result-head">
            <el-tag :type="detectResult.IsQualified ? 'success' : 'danger'" size="medium">
              {{ detectResult.IsQualified ? '合格' : '不合格' }}
            </el-tag>
            <span class="detect-summary">{{ detectResult.Summary }}</span>
          </div>
          <table class="detect-score-table">
            <tr>
              <th>人脸数</th>
              <td>{{ detectResult.FaceCount }}</td>
            </tr>
            <tr>
              <th>人脸概率</th>
              <td>{{ formatDetectProb(detectResult.FaceProbability) }}</td>
            </tr>
            <tr>
              <th>关键点分（≥{{ detectResult.Threshold || 85 }}）</th>
              <td>
                <span :class="scoreClass(detectResult.LandmarkScore)">{{ formatDetectScore(detectResult.LandmarkScore) }}</span>
              </td>
            </tr>
            <tr>
              <th>综合质量（≥{{ detectResult.Threshold || 85 }}）</th>
              <td>
                <span :class="scoreClass(detectResult.QualityScore)">{{ formatDetectScore(detectResult.QualityScore) }}</span>
              </td>
            </tr>
            <tr>
              <th>模糊分</th>
              <td>{{ formatDetectScore(detectResult.BlurScore) }}</td>
            </tr>
            <tr>
              <th>光照分</th>
              <td>{{ formatDetectScore(detectResult.IlluScore) }}</td>
            </tr>
            <tr>
              <th>姿态分</th>
              <td>{{ formatDetectScore(detectResult.PoseScore) }}</td>
            </tr>
            <tr>
              <th>口罩遮挡分</th>
              <td>{{ formatDetectScore(detectResult.MaskScore) }}</td>
            </tr>
          </table>
        </div>
        <div v-else class="detect-loading">
          <p>暂无检测结果</p>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button type="primary" :disabled="detectLoading" @click="detectResultVisible = false">关闭</el-button>
        </span>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import DepartmentPane from "../../components/DepartmentPane";
import Toolbar from "../../components/Toolbar";
import { getStudentInfoListPage } from '../../api/modules/personnel';
import {
    getFaceDbBySchool,
    createFaceDb,
    getPersonFaceStatusList,
    batchAddFace,
    addFace,
    deleteFaceEntity,
    getFaceBatchSyncJobRunning,
    getFaceBatchSyncJobPage,
    getFaceBatchSyncJobItemPage,
    detectFace,
    batchDetectFace,
    searchFaceByFile
} from "../../api/faceBody";

export default {
    name: "FaceManage",
    components: { DepartmentPane, Toolbar },
    data() {
        return {
            currentDept: null,
            /** el-tree 节点引用，用于解析顶级学校部门 Id */
            deptTreeNode: null,
            currentSchoolId: 0,
            currentSchoolName: '',
            /** 机构ID（Department.SchId，如 sch_100），用于人脸库命名 */
            currentSchId: '',
            /** 部门配置是否开启人脸比对（未开通则隐藏同步能力） */
            faceCompareEnabled: false,
            /** 当前部门的人脸配置是否已完成加载，避免切换时出现错误的过渡态 */
            faceDbInfoLoaded: false,
            faceDbInfo: {
                dbName: '',
                createTime: null
            },
            staffList: [],
            /** 列表加载序号，避免快速切部门时旧请求覆盖新结果 */
            staffListLoadSeq: 0,
            listLoading: false,
            creatingDb: false,
            page: 1,
            pageSize: 10,
            total: 0,
            filters: {
                stuName: '',
                cardNo: '',
                isSync: 0
            },
            searchList: [
                { label: '姓名', key: 'stuName' },
                { label: '卡号', key: 'cardNo' },
                {
                    label: '同步状态',
                    key: 'isSync',
                    type: 'select',
                    clearable: false,
                    initValue: 0,
                    options: [
                        { label: '全部状态', value: 0 },
                        { label: '已同步', value: 1 },
                        { label: '未同步', value: 2 },
                        { label: '待更新', value: 3 }
                    ]
                }
            ],
            activeSyncJob: null,
            syncPollTimer: null,
            syncJobList: [],
            syncJobTotal: 0,
            syncJobPage: 1,
            syncJobPageSize: 5,
            jobDetailVisible: false,
            detailJobId: null,
            jobItemList: [],
            jobItemTotal: 0,
            jobItemPage: 1,
            jobItemPageSize: 10,
            /** 同步记录弹窗是否打开 */
            syncRecordDialogVisible: false,
            /** 批量质量检测 */
            batchDetectDialogVisible: false,
            batchDetectLoading: false,
            batchDetectItems: [],
            batchDetectSummary: { TotalCount: 0, QualifiedCount: 0, UnqualifiedCount: 0 },
            batchDetectFilter: 0,
            /** 单人检测 */
            detectLoading: false,
            detectResultVisible: false,
            detectResult: null,
            detectResultTitle: '人脸检测结果',
            /** 上传识别（SearchFaceByFile） */
            searchFaceDialogVisible: false,
            searchFaceLoading: false,
            searchFacePreviewUrl: '',
            searchFaceFile: null,
            searchFaceResult: null
        };
    },
    computed: {
        batchSyncInProgress() {
            return !!(this.activeSyncJob && this.activeSyncJob.Status === 0);
        },
        /** 当前选中节点是否为班级（DeptOrgType=class）。创建于 2026-09-15 */
        isCurrentDeptClass() {
            if (!this.currentDept) return false;
            const t = this.currentDept.DeptOrgType !== undefined
                ? this.currentDept.DeptOrgType
                : this.currentDept.deptOrgType;
            return String(t || '').toLowerCase() === 'class';
        },
        filteredBatchDetectItems() {
            const list = this.batchDetectItems || [];
            if (this.batchDetectFilter === 1) return list.filter(i => i.IsQualified);
            if (this.batchDetectFilter === 2) return list.filter(i => !i.IsQualified);
            return list;
        },
        currentDeptName() {
            if (!this.currentDept) return '-';
            return this.currentDept.Name || this.currentDept.label || '-';
        },
        currentDeptId() {
            if (!this.currentDept) return 0;
            const id = this.currentDept.Id != null ? this.currentDept.Id : this.currentDept.value;
            const n = Number(id);
            return n > 0 ? n : 0;
        },
        /**
         * 工具栏仅保留查询与「按查询同步」，主同步入口在头部配置面板。创建于 2026-08-02
         */
        buttonList() {
            const list = [
                { name: '查询', Func: 'handleQuery', icon: 'el-icon-search' }
            ];
            if (this.faceDbInfoLoaded && this.faceCompareEnabled && this.faceDbInfo.dbName && this.isCurrentDeptClass) {
                list.push({
                    name: '按查询同步',
                    Func: 'handleBatchAddFaceByQueryFilters',
                    icon: 'el-icon-upload2',
                    btnType: 'success'
                });
            }
            return list;
        }
    },
    beforeDestroy() {
        this.stopSyncPolling();
        this.clearSearchFacePreview();
    },
    methods: {
        /**
         * 从部门树向上查找 DeptOrgType 为 school 的节点，得到与 FacePersonRecord.SchoolId 一致的部门主键。
         * 未开通人脸比对时 GetFaceDbBySchool 可能未带回 schoolId，此处保证仍能拉取历史同步状态。
         */
        resolveSchoolDepartmentIdFromTree() {
            const data = this.currentDept;
            const node = this.deptTreeNode;
            if (!data) return 0;
            const idOf = (d) => {
                if (!d) return 0;
                const v = d.Id !== undefined && d.Id !== null ? d.Id : (d.id !== undefined ? d.id : d.value);
                const n = Number(v);
                return n > 0 ? n : 0;
            };
            const isSchool = (d) => {
                if (!d) return false;
                const t = d.DeptOrgType !== undefined ? d.DeptOrgType : d.deptOrgType;
                return t === 'school';
            };
            if (isSchool(data)) return idOf(data);
            let n = node;
            while (n && n.parent) {
                const pd = n.parent.data;
                if (pd && isSchool(pd)) return idOf(pd);
                n = n.parent;
            }
            return 0;
        },

        /**
         * 从部门树解析机构ID（SchId，如 sch_100），优先取 school 节点。创建于 2026-09-14
         */
        resolveSchIdFromTree() {
            const data = this.currentDept;
            const node = this.deptTreeNode;
            if (!data) return '';
            const schIdOf = (d) => {
                if (!d) return '';
                return String(d.SchId || d.schId || '').trim();
            };
            const isSchool = (d) => {
                if (!d) return false;
                const t = d.DeptOrgType !== undefined ? d.DeptOrgType : d.deptOrgType;
                return t === 'school';
            };
            if (isSchool(data)) return schIdOf(data);
            let n = node;
            let fallback = schIdOf(data);
            while (n && n.parent) {
                const pd = n.parent.data;
                if (pd && isSchool(pd)) return schIdOf(pd) || fallback;
                if (!fallback && pd) fallback = schIdOf(pd);
                n = n.parent;
            }
            return fallback;
        },

        /** 统一解析 GetFaceDbBySchool 等接口 payload（兼容 camelCase / PascalCase） */
        normalizeFaceDbPayload(r) {
            if (!r || typeof r !== 'object') {
                return {
                    schoolId: 0,
                    schoolName: '',
                    schId: '',
                    dbName: '',
                    createTime: null,
                    enableFaceCompare: undefined
                };
            }
            const schoolId = Number(r.schoolId !== undefined ? r.schoolId : r.SchoolId) || 0;
            return {
                schoolId: schoolId > 0 ? schoolId : 0,
                schoolName: (r.schoolName !== undefined ? r.schoolName : r.SchoolName) || '',
                schId: String((r.schId !== undefined ? r.schId : r.SchId) || '').trim(),
                dbName: (r.dbName !== undefined ? r.dbName : r.DbName) || '',
                createTime: r.createTime !== undefined ? r.createTime : r.CreateTime,
                enableFaceCompare: r.enableFaceCompare !== undefined ? r.enableFaceCompare : r.EnableFaceCompare
            };
        },

        ensureSchoolContextFromTree() {
            if (!this.currentSchoolId) {
                const sid = this.resolveSchoolDepartmentIdFromTree();
                if (sid) this.currentSchoolId = sid;
            }
            if (!this.currentSchId) {
                const schId = this.resolveSchIdFromTree();
                if (schId) this.currentSchId = schId;
            }
            if (!this.currentSchoolName && this.currentDept) {
                const data = this.currentDept;
                const node = this.deptTreeNode;
                const isSchool = (d) => {
                    if (!d) return false;
                    const t = d.DeptOrgType !== undefined ? d.DeptOrgType : d.deptOrgType;
                    return t === 'school';
                };
                if (isSchool(data) && (data.Name || data.label)) {
                    this.currentSchoolName = data.Name || data.label;
                } else if (node) {
                    let n = node;
                    while (n && n.parent) {
                        const pd = n.parent.data;
                        if (pd && isSchool(pd) && (pd.Name || pd.label)) {
                            this.currentSchoolName = pd.Name || pd.label;
                            break;
                        }
                        n = n.parent;
                    }
                }
            }
        },

        /** Toolbar 统一回调。创建于 2026-08-02 */
        callFunction(item) {
            if (!item || !item.Func) return;
            if (item.Func === 'handleQuery') {
                const search = item.search || {};
                this.filters.stuName = search.stuName || '';
                this.filters.cardNo = search.cardNo || '';
                this.filters.isSync = search.isSync === undefined || search.isSync === '' ? 0 : Number(search.isSync);
                this.handleSearch();
                return;
            }
            if (typeof this[item.Func] === 'function') {
                this[item.Func](item);
            }
        },

        // 树节点点击（第二参数为 el-tree node，勿省略）
        async treeNodeClick(data, node) {
            this.currentDept = data;
            this.deptTreeNode = node || null;
            this.page = 1;

            // 无论是学校还是部门，都调用后端接口来识别顶级学校并获取人脸库
            await this.loadFaceDbInfo(this.currentDeptId);
            this.loadStaffList();
            this.loadActiveSyncJob();
            this.syncRecordDialogVisible = false;
            this.syncJobPage = 1;
        },

        // 加载人脸库信息（未开通人脸比对时接口返回失败但附带 schoolId，便于继续查人员列表）
        async loadFaceDbInfo(id) {
            const targetId = id || this.currentDeptId;
            if (!targetId) return;
            this.faceDbInfoLoaded = false;
            try {
                const res = await getFaceDbBySchool({ departmentId: targetId });
                const body = res.data;
                if (body && body.success && body.response) {
                    const p = this.normalizeFaceDbPayload(body.response);
                    this.faceCompareEnabled = p.enableFaceCompare !== false;
                    this.faceDbInfo = {
                        dbName: p.dbName || '',
                        createTime: p.createTime || null
                    };
                    this.currentSchoolId = p.schoolId;
                    this.currentSchoolName = p.schoolName || '';
                    this.currentSchId = p.schId || '';
                } else {
                    const p = this.normalizeFaceDbPayload(body && body.response);
                    this.faceDbInfo = {
                        dbName: p.dbName || '',
                        createTime: p.createTime || null
                    };
                    this.currentSchoolId = p.schoolId;
                    this.currentSchoolName = p.schoolName || '';
                    this.currentSchId = p.schId || '';
                    this.faceCompareEnabled = false;
                }
                this.ensureSchoolContextFromTree();
            } catch (err) {
                console.error(err);
                this.faceCompareEnabled = false;
                this.faceDbInfo = { dbName: '', createTime: null };
                this.ensureSchoolContextFromTree();
            } finally {
                this.faceDbInfoLoaded = true;
            }
        },

        // 创建人脸库（库名使用机构ID，阿里云仅允许字母/数字/下划线）。最后修改：2026-09-14
        async handleCreateFaceDb() {
            if (!this.faceCompareEnabled || !this.currentSchoolId) return;
            this.ensureSchoolContextFromTree();
            const schId = this.currentSchId || this.resolveSchIdFromTree();
            if (!schId) {
                this.$message.error('当前学校缺少机构ID，无法初始化人脸库');
                return;
            }
            this.currentSchId = schId;
            this.$confirm('确定要为该学校初始化人脸库吗？', '提示', { type: 'info' }).then(async () => {
                this.creatingDb = true;
                try {
                    const res = await createFaceDb({
                        schoolId: this.currentSchoolId,
                        name: "FaceDB_" + schId
                    });
                    if (res.success) {
                        this.$message.success("人脸库初始化成功");
                        this.loadFaceDbInfo();
                    } else {
                        this.$message.error(res.msg);
                    }
                } finally {
                    this.creatingDb = false;
                }
            });
        },

        // 加载人员列表（人脸状态合并后再赋值，避免先闪「未同步」）。最后修改：2026-09-15
        async loadStaffList() {
            if (!this.currentDeptId) return;
            const loadSeq = ++this.staffListLoadSeq;
            this.listLoading = true;
            try {
                const schoolIdForQuery = this.currentSchoolId || this.resolveSchoolDepartmentIdFromTree();
                let res = await getStudentInfoListPage({
                    page: this.page,
                    intPageSize: this.pageSize,
                    departmentId: this.currentDeptId,
                    stuName: this.filters.stuName,
                    cardNo: this.filters.cardNo,
                    isSync: this.filters.isSync,
                    schoolId: schoolIdForQuery
                });

                if (loadSeq !== this.staffListLoadSeq) return;

                if (res && res.data && res.data.success) {
                    const responseData = res.data.response;
                    let list = [];
                    let total = 0;
                    if (responseData.data) {
                        list = responseData.data || [];
                        total = responseData.dataCount;
                    } else {
                        list = responseData || [];
                        total = list.length || 0;
                    }
                    list = await this.attachFaceRecords(list, schoolIdForQuery);
                    if (loadSeq !== this.staffListLoadSeq) return;
                    this.staffList = list;
                    this.total = total;
                }
            } catch (err) {
                console.error(err);
            } finally {
                if (loadSeq === this.staffListLoadSeq) {
                    this.listLoading = false;
                }
            }
        },

        /**
         * 为人脸列表批量挂载同步记录（不直接改 this.staffList，避免中间态闪烁）。创建于 2026-09-15
         */
        async attachFaceRecords(list, schoolId) {
            const source = list || [];
            if (!source.length || !schoolId) {
                return source.map(item => ({ ...item, faceRecord: null }));
            }
            try {
                const res = await getPersonFaceStatusList({
                    schoolId: schoolId,
                    personType: 'Student',
                    personIds: source.map(i => i.Id)
                });
                if (!res || !res.success) {
                    return source.map(item => ({ ...item, faceRecord: null }));
                }
                const records = res.response || [];
                return source.map(item => {
                    const matched = records.filter(r => r.PersonId === item.Id);
                    // 多条时优先已同步成功的记录，避免脏数据把状态打成未同步。最后修改：2026-09-14
                    const record = matched.sort((a, b) => {
                        const aOk = a.HasFaceData ? 1 : 0;
                        const bOk = b.HasFaceData ? 1 : 0;
                        if (bOk !== aOk) return bOk - aOk;
                        return (b.Id || 0) - (a.Id || 0);
                    })[0];
                    return {
                        ...item,
                        faceRecord: record || null
                    };
                });
            } catch (err) {
                console.error("Load Status Error:", err);
                return source.map(item => ({ ...item, faceRecord: null }));
            }
        },

        // 批量刷新当前页人脸同步状态（只读；未开通比对时仍可显示历史同步结果）
        async loadFaceStatus() {
            const schoolId = this.currentSchoolId || this.resolveSchoolDepartmentIdFromTree();
            if (!this.staffList.length || !schoolId) return;
            this.staffList = await this.attachFaceRecords(this.staffList, schoolId);
        },

        handlePageChange(v) {
            this.page = v;
            this.loadStaffList();
        },

        handleSearch() {
            this.page = 1;
            this.loadStaffList();
        },

        /** 打开同步记录弹窗（仅班级；任务按班级 DepartmentId 存储）。最后修改：2026-09-15 */
        openSyncRecordDialog() {
            if (!this.faceCompareEnabled || !this.faceDbInfo.dbName) return;
            if (!this.ensureClassDeptForBatch()) return;
            this.syncRecordDialogVisible = true;
        },
        onSyncRecordDialogOpen() {
            this.syncJobPage = 1;
            this.loadSyncJobList();
        },

        /** 批量操作前校验必须选中班级。创建于 2026-09-15 */
        ensureClassDeptForBatch() {
            if (this.isCurrentDeptClass) return true;
            this.$message.warning('请从左侧选择具体班级后再操作（批量同步 / 检测 / 同步记录均按班级）');
            return false;
        },

        // 批量同步人脸数据（仅班级）。最后修改：2026-09-15
        async handleBatchAddFace() {
            if (!this.faceCompareEnabled || !this.currentDeptId || !this.faceDbInfo.dbName) return;
            if (!this.ensureClassDeptForBatch()) return;
            if (this.batchSyncInProgress) {
                this.$message.warning("当前仍有进行中的人脸同步任务，请待完成后再试");
                return;
            }
            this.$confirm('将对当前班级内有头像的学生全部发起同步，确定开始吗？', '提示', { type: 'info' }).then(async () => {
                this.listLoading = true;
                try {
                    const res = await batchAddFace({
                        schoolId: this.currentSchoolId,
                        departmentId: this.currentDeptId,
                        targetPersonType: 'Student',
                        items: []
                    });
                    if (res && res.success) {
                        this.$message.success(res.msg || "任务已创建");
                        await this.loadActiveSyncJob();
                        this.syncJobPage = 1;
                        if (this.syncRecordDialogVisible) {
                            await this.loadSyncJobList();
                        }
                        this.startSyncPolling();
                    } else {
                        this.$message.error(res ? res.msg : "同步失败");
                    }
                    this.loadStaffList();
                } catch (err) {
                    this.$message.error("程序错误");
                } finally {
                    this.listLoading = false;
                }
            });
        },

        /** 按当前列表查询条件批量同步有头像的学生（仅班级）。最后修改：2026-09-15 */
        async handleBatchAddFaceByQueryFilters() {
            if (!this.faceCompareEnabled || !this.currentDeptId || !this.faceDbInfo.dbName) return;
            if (!this.ensureClassDeptForBatch()) return;
            if (this.batchSyncInProgress) {
                this.$message.warning("当前仍有进行中的人脸同步任务，请待完成后再试");
                return;
            }
            const parts = [];
            if (this.filters.stuName && String(this.filters.stuName).trim())
                parts.push(`姓名含「${String(this.filters.stuName).trim()}」`);
            if (this.filters.cardNo && String(this.filters.cardNo).trim())
                parts.push(`卡号含「${String(this.filters.cardNo).trim()}」`);
            if (this.filters.isSync === 1) parts.push("仅已同步");
            else if (this.filters.isSync === 2) parts.push("仅未同步");
            else if (this.filters.isSync === 3) parts.push("仅待更新");
            else parts.push("全部同步状态");
            const condText = parts.join("，");
            this.$confirm(
                `将按当前查询条件同步有头像的学生（${condText}），范围与上方列表一致且包含所有分页匹配记录。确定开始吗？`,
                "按查询条件同步",
                { type: "info" }
            ).then(async () => {
                this.listLoading = true;
                try {
                    const res = await batchAddFace({
                        schoolId: this.currentSchoolId,
                        departmentId: this.currentDeptId,
                        targetPersonType: "Student",
                        items: [],
                        applyQueryFilters: true,
                        stuName: (this.filters.stuName && String(this.filters.stuName).trim()) || "",
                        cardNo: (this.filters.cardNo && String(this.filters.cardNo).trim()) || "",
                        isSync: this.filters.isSync || 0
                    });
                    if (res && res.success) {
                        this.$message.success(res.msg || "任务已创建");
                        await this.loadActiveSyncJob();
                        this.syncJobPage = 1;
                        if (this.syncRecordDialogVisible) {
                            await this.loadSyncJobList();
                        }
                        this.startSyncPolling();
                    } else {
                        this.$message.error(res ? res.msg : "同步失败");
                    }
                    this.loadStaffList();
                } catch (err) {
                    this.$message.error("程序错误");
                } finally {
                    this.listLoading = false;
                }
            }).catch(() => {});
        },

        async loadActiveSyncJob() {
            if (!this.faceCompareEnabled || !this.currentDeptId || !this.currentSchoolId) {
                this.activeSyncJob = null;
                return;
            }
            try {
                const res = await getFaceBatchSyncJobRunning({
                    SchoolId: this.currentSchoolId,
                    DepartmentId: this.currentDeptId,
                    TargetPersonType: 'Student'
                });
                if (res && res.success) {
                    this.activeSyncJob = res.response || null;
                } else {
                    this.activeSyncJob = null;
                }
            } catch (e) {
                this.activeSyncJob = null;
            }
        },

        async loadSyncJobList() {
            if (!this.faceCompareEnabled || !this.currentDeptId || !this.currentSchoolId) return;
            try {
                const res = await getFaceBatchSyncJobPage({
                    SchoolId: this.currentSchoolId,
                    DepartmentId: this.currentDeptId,
                    TargetPersonType: 'Student',
                    page: this.syncJobPage,
                    intPageSize: this.syncJobPageSize
                });
                if (res && res.success && res.response) {
                    this.syncJobList = res.response.data || [];
                    this.syncJobTotal = res.response.dataCount || 0;
                }
            } catch (e) {
                console.error(e);
            }
        },

        startSyncPolling() {
            this.stopSyncPolling();
            this.syncPollTimer = setInterval(async () => {
                await this.loadActiveSyncJob();
                if (this.syncRecordDialogVisible) {
                    await this.loadSyncJobList();
                }
                if (!this.batchSyncInProgress) {
                    this.stopSyncPolling();
                    this.loadStaffList();
                }
            }, 3000);
        },

        stopSyncPolling() {
            if (this.syncPollTimer) {
                clearInterval(this.syncPollTimer);
                this.syncPollTimer = null;
            }
        },

        openJobDetail(row) {
            this.detailJobId = row.Id;
            this.jobItemPage = 1;
            this.jobDetailVisible = true;
            this.$nextTick(() => this.loadJobItemList());
        },

        async loadJobItemList() {
            if (!this.detailJobId) return;
            try {
                const res = await getFaceBatchSyncJobItemPage({
                    JobId: this.detailJobId,
                    page: this.jobItemPage,
                    intPageSize: this.jobItemPageSize
                });
                if (res && res.success && res.response) {
                    this.jobItemList = res.response.data || [];
                    this.jobItemTotal = res.response.dataCount || 0;
                }
            } catch (e) {
                console.error(e);
            }
        },

        fmtTableTime(row, column, cellValue) {
            if (!cellValue) return '-';
            return this.formatTime(cellValue);
        },

        // 单个同步图片
        async handleSingleAddFace(row) {
            if (!this.faceCompareEnabled) return;
            if (!row.HeadPhotoImage) {
                return this.$message.warning("人员未上传头像，无法同步");
            }
            const personName = row.StuName || '该人员';
            this.$confirm(`确定将「${personName}」的头像同步到人脸库吗？`, '提示', { type: 'info' }).then(async () => {
                this.listLoading = true;
                try {
                    const res = await addFace({
                        schoolId: this.currentSchoolId,
                        personId: row.Id,
                        personType: 'Student',
                        imageUrl: row.HeadPhotoImage
                    });
                    if (res && res.success) {
                        this.$message.success("同步成功");
                        this.loadFaceStatus();
                    } else {
                        this.$message.error(res ? res.msg : "同步失败");
                    }
                } catch (err) {
                    this.$message.error("程序错误，同步失败");
                } finally {
                    this.listLoading = false;
                }
            }).catch(() => {});
        },

        // 删除/重置人脸数据
        async handleDeleteEntity(row) {
            if (!this.faceCompareEnabled) return;
            this.$confirm("重置将从删除该人员的所有人脸数据，确定吗？", "警告", { type: 'warning' }).then(async () => {
                try {
                    const res = await deleteFaceEntity({
                        schoolId: this.currentSchoolId,
                        personId: row.Id,
                        personType: 'Student'
                    });
                    if (res && res.success) {
                        this.$message.success("重置成功");
                        this.loadFaceStatus();
                    } else {
                        this.$message.error(res ? res.msg : "重置失败");
                    }
                } catch (err) {
                    this.$message.error("程序错误，重置失败");
                }
            });
        },

        /**
         * 对当前班级有头像学生批量质量检测。最后修改：2026-09-15
         */
        async handleBatchDetectFace() {
            if (!this.faceCompareEnabled || !this.currentDeptId || !this.faceDbInfo.dbName) return;
            if (!this.ensureClassDeptForBatch()) return;
            if (!this.currentSchoolId) {
                return this.$message.warning('未识别当前学校');
            }
            if (this.batchDetectLoading) return;
            this.$confirm(
                '将对当前班级内有头像的在读学生批量做质量检测（仅查询展示，不入库），确定开始吗？',
                '批量检测',
                { type: 'info' }
            ).then(async () => {
                this.batchDetectItems = [];
                this.batchDetectSummary = { TotalCount: 0, QualifiedCount: 0, UnqualifiedCount: 0 };
                this.batchDetectFilter = 0;
                this.batchDetectLoading = true;
                this.batchDetectDialogVisible = true;
                try {
                    const res = await batchDetectFace({
                        schoolId: this.currentSchoolId,
                        departmentId: this.currentDeptId
                    });
                    if (res && res.success && res.response) {
                        const data = res.response;
                        this.batchDetectSummary = {
                            TotalCount: data.TotalCount || 0,
                            QualifiedCount: data.QualifiedCount || 0,
                            UnqualifiedCount: data.UnqualifiedCount || 0
                        };
                        this.batchDetectItems = data.Items || [];
                        this.$message.success(res.msg || '检测完成');
                    } else {
                        this.batchDetectDialogVisible = false;
                        this.$message.error(res ? res.msg : '批量检测失败');
                    }
                } catch (err) {
                    this.batchDetectDialogVisible = false;
                    this.$message.error('批量检测请求失败');
                } finally {
                    this.batchDetectLoading = false;
                }
            }).catch(() => {});
        },
        /** 列表行：检测该生头像质量。创建于 2026-09-15 */
        async handleDetectRow(row) {
            if (!this.faceCompareEnabled) return;
            if (!row.HeadPhotoImage) {
                return this.$message.warning('人员未上传头像，无法检测');
            }
            if (!this.currentSchoolId) {
                return this.$message.warning('未识别当前学校');
            }
            const name = row.StuName || '该人员';
            this.beginDetect(`「${name}」头像检测结果`);
            try {
                const res = await detectFace({
                    schoolId: this.currentSchoolId,
                    personId: row.Id,
                    personType: 'Student'
                });
                if (res && res.success && res.response) {
                    this.detectResult = res.response;
                } else {
                    this.detectResultVisible = false;
                    this.$message.error(res ? res.msg : '检测失败');
                }
            } catch (err) {
                this.detectResultVisible = false;
                this.$message.error('检测请求失败');
            } finally {
                this.detectLoading = false;
            }
        },
        /** 立刻弹出检测中弹窗。创建于 2026-09-15 */
        beginDetect(title) {
            this.detectResult = null;
            this.detectResultTitle = title || '人脸检测';
            this.detectLoading = true;
            this.detectResultVisible = true;
        },
        formatDetectScore(v) {
            if (v === null || v === undefined || v === '') return '—';
            const n = Number(v);
            return Number.isFinite(n) ? n.toFixed(2) : '—';
        },
        formatDetectProb(v) {
            if (v === null || v === undefined || v === '') return '—';
            const n = Number(v);
            if (!Number.isFinite(n)) return '—';
            return `${(n * 100).toFixed(1)}%`;
        },
        scoreClass(v) {
            if (v === null || v === undefined || v === '') return 'score-missing';
            const n = Number(v);
            if (!Number.isFinite(n)) return 'score-missing';
            return n >= 85 ? 'score-ok' : 'score-bad';
        },

        /** 打开上传识别弹窗。创建于 2026-09-15 */
        openSearchFaceDialog() {
            if (!this.faceCompareEnabled || !this.faceDbInfo.dbName) return;
            this.searchFaceResult = null;
            this.clearSearchFacePreview();
            this.searchFaceDialogVisible = true;
        },
        onSearchFaceDialogClosed() {
            this.clearSearchFacePreview();
            this.searchFaceResult = null;
            this.searchFaceLoading = false;
        },
        clearSearchFacePreview() {
            if (this.searchFacePreviewUrl && String(this.searchFacePreviewUrl).indexOf('blob:') === 0) {
                try { URL.revokeObjectURL(this.searchFacePreviewUrl); } catch (e) { /* ignore */ }
            }
            this.searchFacePreviewUrl = '';
            this.searchFaceFile = null;
        },
        onSearchFaceFileChange(file) {
            const raw = file && file.raw ? file.raw : null;
            if (!raw) return;
            this.clearSearchFacePreview();
            this.searchFaceFile = raw;
            this.searchFacePreviewUrl = URL.createObjectURL(raw);
            this.searchFaceResult = null;
        },
        /** 调用 SearchFaceByFile 做人脸库检索。创建于 2026-09-15 */
        async submitSearchFace() {
            if (!this.searchFaceFile) {
                return this.$message.warning('请先选择图片');
            }
            const schId = this.currentSchId || this.resolveSchIdFromTree();
            if (!schId) {
                return this.$message.warning('未识别学校机构ID（SchId）');
            }
            this.searchFaceLoading = true;
            this.searchFaceResult = null;
            try {
                const formData = new FormData();
                formData.append('file', this.searchFaceFile);
                formData.append('schoolId', schId);
                formData.append('departmentId', this.currentSchoolId || this.currentDeptId || 0);
                const res = await searchFaceByFile(formData);
                if (res && res.success && res.response) {
                    this.searchFaceResult = res.response;
                    this.$message.success(res.msg || '匹配成功');
                } else {
                    this.$message.error(res ? res.msg : '识别失败');
                }
            } catch (err) {
                this.$message.error('识别请求失败');
            } finally {
                this.searchFaceLoading = false;
            }
        },

        formatTime(t) {
            if (!t) return '-';
            const d = new Date(t);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hour = String(d.getHours()).padStart(2, '0');
            const minute = String(d.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day} ${hour}:${minute}`;
        },
        /** 头像路径规范化（去 ?v= / 预签名参数），与后端判定一致。创建于 2026-09-14 */
        normalizePhotoPath(url) {
            if (!url) return '';
            let s = String(url).trim();
            // 多段头像只比第一段
            if (s.indexOf(';') >= 0) s = s.split(';').map(x => x.trim()).filter(Boolean)[0] || '';
            const vIdx = s.indexOf('?v=');
            if (vIdx > 0) s = s.slice(0, vIdx);
            const qIdx = s.indexOf('?');
            if (qIdx > 0) s = s.slice(0, qIdx);
            return s;
        },
        /** 同步状态：1 已同步，2 未同步，3 待更新。创建于 2026-09-14 */
        resolveFaceSyncStatus(row) {
            const record = row && row.faceRecord;
            if (!record || !record.HasFaceData) return 2;
            const synced = this.normalizePhotoPath(record.SyncedImageUrl);
            if (!synced) return 1;
            const current = this.normalizePhotoPath(row.HeadPhotoImage);
            if (synced.toLowerCase() !== (current || '').toLowerCase()) return 3;
            return 1;
        },
        getStatusType(row) {
            const status = this.resolveFaceSyncStatus(row);
            if (status === 1) return 'success';
            if (status === 3) return 'warning';
            return 'info';
        },
        getStatusText(row) {
            const status = this.resolveFaceSyncStatus(row);
            if (status === 1) return '已同步';
            if (status === 3) return '待更新';
            return '未同步';
        }
    }
};
</script>

<style scoped>
/* 形成 BFC，清掉 Toolbar 内 el-col 浮动。创建于 2026-08-02 */
.face-right-body {
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.status-panel,
.face-right-body >>> .toolbar {
  flex-shrink: 0;
}

.face-right-body > .tbl-scroll-host {
  flex: 1 1 auto;
  min-height: 0;
  height: 0;
  width: 100%;
  overflow: hidden;
}

.face-right-body > .pagination-container {
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .face-right-body {
    overflow: visible;
    height: auto;
  }
  .face-right-body > .tbl-scroll-host {
    height: auto;
    flex: none;
    overflow: visible;
  }
}

/* 人脸库状态条：学校、库名称、当前部门同一行。最后修改：2026-10-14 */
.status-panel {
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.status-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  min-height: 32px;
}

.status-row .title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.status-row .title i {
  color: var(--btn-primary, #2563eb);
  font-size: 16px;
}

.status-row .sub-title {
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
}

.info-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.info-item .info-label {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
}

.info-item .info-value {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.info-item .info-value.is-empty {
  color: #94a3b8;
  font-weight: 400;
}

.info-action-btns {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.face-inline-tip {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #b45309;
}

.face-inline-tip i {
  margin-top: 2px;
  flex-shrink: 0;
}

.face-inline-tip.is-info {
  color: #64748b;
}

.no-data-tip {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  color: #c0c4cc;
}

.no-data-tip i {
  font-size: 48px;
  margin-bottom: 12px;
}

.muted {
  color: #c0c4cc;
}

.pagination-container {
  clear: both;
  overflow: hidden;
  margin-top: 16px;
}

.sync-dialog-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 32px;
  font-weight: 600;
  color: #303133;
  font-size: 15px;
}

.sync-dialog-title i {
  margin-right: 6px;
  color: var(--btn-primary, #2563eb);
}

.sync-record-pager {
  margin-top: 10px;
  text-align: right;
}

@media (max-width: 900px) {
  .info-action-btns {
    margin-left: 0;
  }
}

.detect-hint {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
}

.detect-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  color: #606266;
  font-size: 14px;
}

.detect-loading i {
  font-size: 28px;
  color: var(--btn-primary, #2563eb);
  margin-bottom: 12px;
}

.detect-loading-sub {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
}

.batch-detect-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}

.detect-result-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
}

.detect-summary {
  flex: 1;
  font-size: 13px;
  line-height: 1.5;
  color: #606266;
}

.detect-score-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.detect-score-table th,
.detect-score-table td {
  border: 1px solid #ebeef5;
  padding: 8px 12px;
  text-align: left;
}

.detect-score-table th {
  width: 42%;
  background: #fafafa;
  color: #606266;
  font-weight: 500;
}

.score-ok {
  color: #67c23a;
  font-weight: 600;
}

.score-bad {
  color: #f56c6c;
  font-weight: 600;
}

.score-missing {
  color: #909399;
}

.search-face-tip {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #606266;
}

.search-face-tip-sub {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
}

.search-face-uploader {
  width: 100%;
}

.search-face-uploader >>> .el-upload {
  width: 100%;
}

.search-face-uploader >>> .el-upload-dragger {
  width: 100%;
  height: auto;
  min-height: 180px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-face-preview-wrap {
  width: 100%;
  text-align: center;
}

.search-face-media {
  max-width: 100%;
  max-height: 280px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
  border-radius: 4px;
}

.search-face-result {
  margin-top: 14px;
  border: 1px solid #e1f3d8;
  background: #f0f9eb;
  border-radius: 8px;
  padding: 12px;
}

.search-face-result-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.search-face-result-info {
  font-size: 13px;
  line-height: 1.7;
  color: #303133;
}
</style>

<style>
/* append-to-body：固定表格高度，少数据也不压扁。最后修改：2026-09-14 */
.el-dialog.face-sync-record-dialog {
  max-width: calc(100vw - 32px);
  border-radius: 12px;
  overflow: hidden;
}

.el-dialog.face-sync-record-dialog > .el-dialog__body {
  max-height: none !important;
  overflow: hidden !important;
  padding: 14px 18px 16px;
}

.el-dialog.face-sync-record-dialog .face-sync-record-table .el-table__fixed-body-wrapper,
.el-dialog.face-sync-record-dialog .face-sync-record-table .el-table__fixed-right .el-table__fixed-body-wrapper {
  overflow-y: hidden !important;
}
</style>
